/**
 * ActionCard — Icon + title + description, pressable
 *
 * Used for: merchant dashboard quick actions (add product, scan, settings)
 */
import type { ReactNode } from 'react'
import { styled, YStack, Text as TamaguiText, type GetProps } from 'tamagui'
import { IconBadge } from '../elements/IconBadge'

const ActionCardFrame = styled(YStack, {
  name: 'ActionCard',
  tag: 'button',
  cursor: 'pointer',
  userSelect: 'none',
  bg: '$backgroundCard',
  br: '$lg',
  p: '$md',
  ai: 'center',
  gap: '$sm',
  minWidth: 120,
  animation: 'snap',
  pressStyle: { opacity: 0.85, scale: 0.98 },
  hoverStyle: { opacity: 0.9 },
})

const ActionTitle = styled(TamaguiText, {
  name: 'ActionTitle',
  fontFamily: '$body',
  fontSize: 13,
  fontWeight: '600',
  color: '$color',
})

const ActionDesc = styled(TamaguiText, {
  name: 'ActionDesc',
  fontFamily: '$body',
  fontSize: 11,
  color: '$colorSecondary',
})

type ActionCardFrameProps = GetProps<typeof ActionCardFrame>

interface ActionCardProps extends ActionCardFrameProps {
  icon: ReactNode
  title: string
  description?: string
}

export const ActionCard = ActionCardFrame.styleable<ActionCardProps>(
  ({ icon, title, description, onPress, ...props }, ref) => {
    return (
      <ActionCardFrame ref={ref} onPress={onPress} {...props}>
        <IconBadge bg="$colorPrimary">{icon}</IconBadge>
        <ActionTitle>{title}</ActionTitle>
        {description && <ActionDesc>{description}</ActionDesc>}
      </ActionCardFrame>
    )
  },
)
